import * as React from 'react';
import { Row, Column } from '@narvar/nth-grid';
import { useTheme, withThemeProvider } from '@narvar/nth-hook-theme';
import { MediumHeader, SemibigHeader } from '@narvar/nth-bit-header';
import TextLink from '@narvar/nth-bit-text-link';
import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarClickTypes,
} from '@narvar/nth-hook-context-analytics';
import PickupLocationMap from './PickupLocationMap';
import StoreDetails from './StoreDetails';
import useTempTranslations from '../useTempTranslations';
import { LocationDetail } from '../../../../server/api/schema';
import PickupInstructions from './PickupInstructions';

interface PickupLocationTemplateProps {
  featureName?: string;
  storeLocation?: LocationDetail | null;
}

// TODO: move these into nth-hook-context-analytics,
// as upgrading to @latest causes react dep conflicts
const NTH_PACK_PICKUP_POINT = 'nth-pack-pickup-point';
const NTH_PACK_REDIRECT_TO_HOLD = 'nth-pack-redirect-to-hold';
const PICKUP_POINT_SHOW_PICKUP_DETAILS_LINK =
  'pickup_point_show_pickup_details_link';
const PICKUP_POINT_HIDE_PICKUP_DETAILS_LINK =
  'pickup_point_hide_pickup_details_link';
const REDIRECT_TO_HOLD_SHOW_PICKUP_DETAILS_LINK =
  'redirect_to_hold_show_pickup_details_link';
const REDIRECT_TO_HOLD_HIDE_PICKUP_DETAILS_LINK =
  'redirect_to_hold_hide_pickup_details_link';

const PickupLocationTemplate: React.FunctionComponent<
  PickupLocationTemplateProps
> = ({ featureName, storeLocation }: PickupLocationTemplateProps) => {
  const [showPickupDetails, setShowPickupDetails] = React.useState(false);

  const logEvent = useAnalyticsPost();

  const {
    bodytextDefaultNone: textStyle,
    linksNoneDefault: linkDefault,
    linksNoneHover: linkHover,
  } = useTheme();

  // TODO: update these values in nth-hook-translations and use that.
  // currently installing @latest results in react dependency conflicts.
  const {
    locationPanelTitle,
    redirectToHoldPostSuccessHeader,
    pickupLocationHideDetails,
    pickupLocationShowDetails,
  } =
    useTempTranslations({
      translationKeys: [
        'locationPanelTitle',
        'redirectToHoldPostSuccessHeader',
        'pickupLocationHideDetails',
        'pickupLocationShowDetails',
      ],
    }) || {};

  if (!storeLocation) return null;

  const isRth = featureName === 'redirectToHold';

  const eventComponentName = isRth
    ? NTH_PACK_REDIRECT_TO_HOLD
    : NTH_PACK_PICKUP_POINT;
  const showDetailsLink = isRth
    ? REDIRECT_TO_HOLD_SHOW_PICKUP_DETAILS_LINK
    : PICKUP_POINT_SHOW_PICKUP_DETAILS_LINK;
  const hideDetailsLink = isRth
    ? REDIRECT_TO_HOLD_HIDE_PICKUP_DETAILS_LINK
    : PICKUP_POINT_HIDE_PICKUP_DETAILS_LINK;

  const { name, address, geocode } = storeLocation || {};

  const addressLine1 = `${address?.street1}${address?.street2 ? ', ' : ''}${
    address?.street2 || ''
  }`;
  const addressLine2 = `${address?.city}${address?.city ? ', ' : ''}${
    address?.stateOrProvince
  } ${address?.postalCode}`;

  return (
    <>
      {showPickupDetails ? (
        <>
          <Row>
            <Column>
              <SemibigHeader
                as="h2"
                containerCustomStyle={{ marginBottom: '1rem' }}
              >
                {isRth ? redirectToHoldPostSuccessHeader : locationPanelTitle}
              </SemibigHeader>
            </Column>
          </Row>
          <Row>
            <Column xs-columns={12} sm-columns={4}>
              <PickupLocationMap featureName={featureName} geocode={geocode} />
            </Column>
            <Column
              xs-columns={12}
              sm-columns={4}
              xs-style="border-bottom: 1px solid #e0e0e0; margin-top: 1rem; margin-bottom: 1rem;"
              sm-style="border-right: 1px solid #e0e0e0; border-bottom: 0; margin-top: 0;"
            >
              <StoreDetails
                featureName={featureName}
                storeLocation={storeLocation}
              />
            </Column>
            <Column
              xs-columns={12}
              sm-columns={4}
              xs-style="margin: 1rem 0 1rem 0;"
              sm-style="margin: 0 -1rem 0 1rem;"
            >
              <PickupInstructions isRth={isRth} />
            </Column>
          </Row>
        </>
      ) : (
        <Row>
          <Column xs-columns={12} sm-columns={9}>
            <SemibigHeader as="h2">
              {isRth ? redirectToHoldPostSuccessHeader : locationPanelTitle}
            </SemibigHeader>
          </Column>
          <Column
            xs-columns={12}
            sm-columns={3}
            xs-style="margin-top: 1rem;"
            sm-style="margin-top: 0;"
          >
            {name && <MediumHeader as="h3">{name}</MediumHeader>}
            {address && (
              <div style={textStyle}>
                {addressLine1 && <div>{addressLine1}</div>}
                {addressLine2 && <div>{addressLine2}</div>}
                {}
              </div>
            )}
          </Column>
        </Row>
      )}
      <Row>
        <Column xs-columns={12}>
          <div style={{ marginTop: '1rem' }}>
            <TextLink
              linkStyle={{ sDefault: linkDefault, sHover: linkHover }}
              onClick={(): void => {
                setShowPickupDetails(!showPickupDetails);
                const clickDescription = showPickupDetails
                  ? hideDetailsLink
                  : showDetailsLink;
                logEvent({
                  clickCategory: clickDescription,
                  clickLinkLabel: showPickupDetails
                    ? pickupLocationHideDetails
                    : pickupLocationShowDetails,
                  clickSubType: clickDescription,
                  clickType: NarvarClickTypes.INTERACTION,
                  eventComponent: eventComponentName,
                  eventFeature: eventComponentName,
                  eventType: NarvarEventTypes.CLICK,
                  feature: featureName,
                  pageComponent: eventComponentName,
                });
              }}
              aria-expanded={showPickupDetails}
              aria-controls="pickup-point-details"
            >
              {showPickupDetails
                ? pickupLocationHideDetails
                : pickupLocationShowDetails}
            </TextLink>
          </div>
        </Column>
      </Row>
    </>
  );
};

export default withThemeProvider(PickupLocationTemplate, {
  themeTokens: ['bodytextDefaultNone', 'linksNoneDefault', 'linksNoneHover'],
});
